import { createPortal } from 'react-dom';
import { LogOut, ShieldCheck } from 'lucide-react';

interface LogoutLoaderProps {
  message?: string;
  subMessage?: string;
}

export function LogoutLoader({
  message = 'Logging Out',
  subMessage = 'Please wait while we end your session...',
}: LogoutLoaderProps) {
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xs select-none"
      role="alertdialog"
      aria-modal="true"
      aria-label="Logging out"
    >
      <div
        className="relative w-full max-w-sm overflow-hidden rounded-2xl border border-gray-100 bg-white p-7 text-center shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Subtle decorative top bar */}
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#4369a0] via-[#3a5d8f] to-teal-500" />

        {/* Ambient background glows */}
        <div className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-[#4369a0]/10 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-8 -left-8 h-28 w-28 rounded-full bg-teal-500/10 blur-2xl" />

        {/* Center animated spinner & icon */}
        <div className="relative mx-auto mb-4 flex h-16 w-16 items-center justify-center">
          {/* Subtle pulse ring */}
          <div className="absolute inset-0 rounded-full bg-[#4369a0]/10 animate-ping opacity-75" />

          {/* Outer ring background */}
          <div className="absolute inset-0 rounded-full border-[3px] border-[#4369a0]/15" />

          {/* Spinning ring */}
          <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-[#4369a0] border-r-[#4369a0] animate-spin" />

          {/* Center icon badge */}
          <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-slate-50 text-[#4369a0] shadow-xs ring-1 ring-gray-100">
            <LogOut className="h-4 w-4 text-[#4369a0]" />
          </div>
        </div>

        {/* Text */}
        <h3 className="text-base font-semibold tracking-tight text-gray-900">
          {message}
        </h3>
        <p className="mt-1 text-xs text-gray-500">
          {subMessage}
        </p>

        {/* Animated bounce dots */}
        <div className="mt-4 flex items-center justify-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-[#4369a0] animate-bounce [animation-delay:-0.3s]" />
          <span className="h-1.5 w-1.5 rounded-full bg-[#4369a0] animate-bounce [animation-delay:-0.15s]" />
          <span className="h-1.5 w-1.5 rounded-full bg-[#4369a0] animate-bounce" />
        </div>

        {/* Bottom security badge */}
        <div className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-gray-100 bg-gray-50/80 px-3 py-1 text-[11px] font-medium text-gray-500">
          <ShieldCheck className="h-3.5 w-3.5 text-[#4369a0]" />
          <span>Securely ending session</span>
        </div>
      </div>
    </div>,
    document.body
  );
}
